import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getReplenishmentRequestDetails,
  updateReplenishmentRequestItemQuantity,
  updateReplenishmentRequestStatus,
} from "../../services/AllServices";
import { showMessage, debounce } from "../../utils/helpers";

const useReplenishmentRequestDetailsViewController = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [sublocationName, setSublocationName] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [requestItems, setRequestItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [requestId, setRequestId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [filter, setFilter] = useState("");
  const pageSize = 15;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalFilteredCount / pageSize)),
    [totalFilteredCount, pageSize]
  );

  useEffect(() => {
    setRequestId(id);
  }, [id]);

  const filterItems = useCallback(
    debounce((items, search, categoryFilter) => {
      setSearchLoading(true);
      try {
        let filtered = items;

        if (search) {
          const isNumber = !isNaN(search);
        
          filtered = items.filter((item) => {
            if (isNumber) {
              return item.sku_id.toString().includes(search);
            } else {
              return item.sku_name.toLowerCase().includes(search.toLowerCase());
            }
          });
        }

        if (categoryFilter) {
          filtered = filtered.filter(
            (item) => item.category.toLowerCase() === categoryFilter.toLowerCase()
          );
        }

        setTotalFilteredCount(filtered.length);

        
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        setFilteredItems(filtered.slice(start, end));
      } finally {
        setSearchLoading(false);
      }
    }, 500),
    [currentPage, pageSize]
  );

  useEffect(() => {
    filterItems(allItems, searchText, filter);
  }, [filterItems, allItems, searchText, filter, currentPage]);

  const loadAllItems = async () => {
    setLoading(true);
    try {
      let allResults = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await getReplenishmentRequestDetails(
          requestId,
          page,
          100
        );

        if (response?.data?.request_items?.length > 0) {
          const items = response.data.request_items.map((item) => ({
            ...item,
            editedQuantity: item.replenishment_quantity,
            isEditing: false,
          }));
          allResults = [...allResults, ...items];

          if (page === 1) {
            setApprovalStatus(response.data.request_approval_status);
            setCreatedAt(response.data.created_at);
            setSublocationName(response.data.store_name);
            setUpdatedAt(response.data?.updated_at);
          }

          if (items.length < 100) {
            hasMore = false;
          }
          page++;
        } else {
          hasMore = false;
        }
      }

      allResults.sort((a, b) =>
        a.sku_name.localeCompare(b.sku_name, undefined, { sensitivity: "base" })
      );

      setAllItems(allResults);
      setTotalCount(allResults.length);
      
      filterItems(allResults, searchText, filter);
    } catch (error) {
      console.error("Error fetching all items:", error);
      showMessage("error", "Error fetching request details");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (requestId) {
      loadAllItems();
    }
  }, [requestId]);

  const handlePageChange = (page) => {
    if (page <= totalPages && page >= 1) {
      setCurrentPage(page);
    }
  };

  const toggleIsEditing = (id) => {
    const updatedItems = filteredItems.map((item) => ({
      ...item,
      isEditing: item.id === id ? !item.isEditing : item.isEditing,
    }));

    setFilteredItems(updatedItems);

    const updatedAllItems = allItems.map((item) => ({
      ...item,
      isEditing: item.id === id ? !item.isEditing : item.isEditing,
    }));
    setAllItems(updatedAllItems);
  };

  const handleQuantityChange = (id, value) => {
    const updatedItems = filteredItems.map((item) => ({
      ...item,
      editedQuantity: item.id === id ? value : item.editedQuantity,
    }));

    setFilteredItems(updatedItems);

    const updatedAllItems = allItems.map((item) => ({
      ...item,
      editedQuantity: item.id === id ? value : item.editedQuantity,
    }));
    setAllItems(updatedAllItems);
  };

  const updateQuantity = async (id, quantity) => {
    try {
      await updateReplenishmentRequestItemQuantity([
        { id: id, quantity: quantity },
      ]);
      // showMessage("success", "Quantity updated successfully");
    } catch (error) {
      console.error("Error updating quantity:", error);
      showMessage("error", "Error updating quantity");
    }
  };

  const updateRequestStatus = async (isApproved) => {
    try {
      const response = await updateReplenishmentRequestStatus(
        requestId,
        isApproved
      );
      if (response.success) {
        showMessage("success", "Status changed successfully");
        goBack();
      }
    } catch (error) {
      showMessage(
        "error",
        error?.response?.data?.message || "Something went wrong"
      );
    }
  };

  const handleApprove = () => updateRequestStatus(true);
  const handleReject = () => updateRequestStatus(false);
  const goBack = () => navigate("/replenishment-requests");
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return {
    requestId,
    loading,
    searchLoading,
    currentPage,
    totalPages,
    handlePageChange,
    toggleIsEditing,
    handleKeyDown,
    requestItems: allItems,
    filteredItems,
    createdAt,
    updatedAt,
    sublocationName,
    approvalStatus,
    updateQuantity,
    handleQuantityChange,
    goBack,
    handleApprove,
    handleReject,
    searchText,
    setSearchText: (text) => setSearchText(text),
    totalFilteredCount,
    totalCount,
    filter,
    setFilter,
  };
};

export default useReplenishmentRequestDetailsViewController;